// AssetArpTable.tsx
import { useEffect, useState } from "react";
import { Network, Loader2, RefreshCw, Search } from "lucide-react";
import { showError } from "../../../utils/toast";
import baseapi from "../../../services/baseapi";

interface ArpEntry {
    id: number;
    ip_address: string;
    mac_address: string;
    interface: string | null;
    vlan: string | null;
    age: number;
    type: string;
    updated: string;
    device_vendor?: string;
}

export default function AssetArpTable({ asset_id, client_id }: { asset_id: any, client_id: any }) {
    const [data, setData] = useState<ArpEntry[]>([]);


    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await baseapi.get("/asset_arp_table", {
                params: {
                    query: JSON.stringify({ asset_id, client_id }),
                },
            });
            if (res.data.status) {
                setData(res.data.data ?? []);

            } else {
                showError("Failed to load ARP table");
            }
        } catch (e: any) {
            showError(e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, [asset_id]);

    const filtered = data.filter(row =>
        [row.ip_address, row.mac_address, row.type, row.interface, row.vlan]
            .some(v => v?.toLowerCase().includes(search.toLowerCase()))
    );

    const TypeBadge = ({ type }: { type: string }) => {
        const style =
            type === "dynamic"
                ? "bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-400"
                : type === "static"
                    ? "bg-green-500/10 border-green-500/20 text-green-600 dark:text-green-400"
                    : "bg-slate-100 dark:bg-gray-700/50 border-slate-200 dark:border-gray-600/40 text-slate-700 dark:text-gray-200";
        return (
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border uppercase ${style}`}>
                {type}
            </span>
        );
    };

    return (
        <div>

            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3">
                <div className="">
                    <Network size={14} className="text-blue-500 dark:text-blue-400" />
                </div>
                <h3 className="text-[13px] font-black text-gray-900 dark:text-gray-200 uppercase tracking-wider">ARP Table</h3>
                {/* {!loading && (
                    <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-slate-700/60 border border-slate-600/40 text-gray-200">
                        {filtered.length} / {total} entries
                    </span>
                )} */}

                {/* Search */}
                <div className="relative ml-auto">
                    <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 dark:text-gray-200" />
                    <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search..."
                        className="bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600/50 rounded-lg pl-6 pr-3 py-1 text-[10px] text-gray-900 dark:text-gray-200 placeholder-slate-400 focus:outline-none focus:border-blue-500/50 w-36"
                    />
                </div>

                {/* Refresh */}
                <button
                    onClick={fetchData}
                    disabled={loading}
                    className="p-1.5 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600/40 rounded-lg text-slate-500 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all group"
                >
                    <RefreshCw size={12} className={loading ? "animate-spin text-blue-500 dark:text-blue-400" : "group-hover:text-blue-500 dark:group-hover:text-blue-400"} />
                </button>
            </div>

            {/* Table */}
            {loading ? (
                <div className="flex items-center justify-center gap-2 py-16">
                    <Loader2 size={16} className="animate-spin text-blue-500 dark:text-blue-400" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-gray-200">Loading ARP table...</span>
                </div>
            ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 py-16 opacity-40">
                    <Network size={22} className="text-slate-400 dark:text-gray-200" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-gray-200">No entries found</span>
                </div>
            ) : (
                <div className="overflow-x-auto border border-slate-200 dark:border-slate-800 rounded-xl bg-white dark:bg-slate-900/80 shadow-sm">
                    <table className="w-full text-left">
                        <thead className="rounded-xl">
                            <tr className="bg-slate-50 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-700">
                                {["IP Address", "MAC Address", "Vendor", "Type", "Last Updated"].map(h => (
                                    <th key={h} className="px-4 py-2.5 text-[9px] font-black text-slate-500 dark:text-gray-200 uppercase tracking-widest whitespace-nowrap">
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                            {filtered.map((row) => (
                                <tr
                                    key={row.id}
                                    className="hover:bg-slate-50 dark:hover:bg-slate-700/20 transition-colors"
                                >


                                    {/* IP */}
                                    <td className="px-4 py-2.5">
                                        <span className="text-[11px] font-mono font-bold text-blue-600 dark:text-blue-300">{row.ip_address}</span>
                                    </td>

                                    {/* MAC */}
                                    <td className="px-4 py-2.5">
                                        <span className="text-[11px] font-mono text-gray-900 dark:text-gray-200 tracking-wide">{row.mac_address}</span>
                                    </td>

                                    <td className="px-4 py-2.5">
                                        <span className="text-[11px] font-mono text-slate-700 dark:text-gray-200 tracking-wide">{row.device_vendor || '-'}</span>
                                    </td>

                                    {/* Type */}
                                    <td className="px-4 py-2.5">
                                        {row.type ? <TypeBadge type={row.type} /> : <span className="text-[10px] text-slate-400 dark:text-gray-200/20">—</span>}
                                    </td>

                                    {/* Updated */}
                                    <td className="px-4 py-2.5">
                                        <span className="text-[10px] text-slate-500 dark:text-gray-200 whitespace-nowrap">
                                            {new Date(row.updated).toLocaleString("en-IN", {
                                                day: "2-digit", month: "short", year: "numeric",
                                                hour: "2-digit", minute: "2-digit",
                                            })}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
